import React from "react";
import { Check, XCircle } from "lucide-react";
import { cn } from "../../utils/cn";

export type ActionButtonSize = "lg" | "md" | "sm" | "xs";

export interface BaseActionButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  disabled?: boolean;
  iconOnly?: boolean;
  size?: ActionButtonSize;
  label?: string;
  icon?: React.ReactNode;
  className?: string;
}

export interface ApproveButtonProps extends BaseActionButtonProps {
  onApprove?: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
}

export interface RejectButtonProps extends BaseActionButtonProps {
  onReject?: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
}

export interface ApproveRejectActionsProps {
  onApprove: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  onReject: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  loading?: boolean;
  approveLoading?: boolean;
  rejectLoading?: boolean;
  disabled?: boolean;
  iconOnly?: boolean;
  rejectIconOnly?: boolean;
  approveIconOnly?: boolean;
  size?: ActionButtonSize;
  approveLabel?: string;
  rejectLabel?: string;
  approveIcon?: React.ReactNode;
  rejectIcon?: React.ReactNode;
  className?: string;
  approveClassName?: string;
  rejectClassName?: string;
}

const SIZE_STYLES: Record<
  ActionButtonSize,
  {
    textBtn: string;
    iconBtn: string;
    icon: string;
  }
> = {
  lg: {
    textBtn: "h-11 px-6 rounded-[12px] text-base",
    iconBtn: "w-11 h-11 rounded-[12px]",
    icon: "w-5 h-5",
  },
  md: {
    textBtn: "h-10 px-5 rounded-[11px] text-sm",
    iconBtn: "w-10 h-10 rounded-[11px]",
    icon: "w-4 h-4",
  },
  sm: {
    textBtn: "h-8 px-3.5 rounded-[9px] text-xs",
    iconBtn: "w-8 h-8 rounded-[9px]",
    icon: "w-3.5 h-3.5",
  },
  xs: {
    textBtn: "h-7 px-2.5 rounded-[8px] text-[11px]",
    iconBtn: "w-7 h-7 rounded-[8px]",
    icon: "w-3 h-3",
  },
};

export const ApproveButton: React.FC<ApproveButtonProps> = ({
  onApprove,
  onClick,
  loading = false,
  disabled = false,
  iconOnly = false,
  size = "md",
  label = "Approve",
  icon,
  className = "",
  children,
  ...rest
}) => {
  const isBlocked = disabled || loading;
  const sizeConfig = SIZE_STYLES[size] || SIZE_STYLES.md;
  const iconElement = icon || <Check className={sizeConfig.icon} />;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isBlocked) return;
    if (onApprove) onApprove(e);
    if (onClick) onClick(e);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isBlocked}
      aria-label={label}
      className={cn(
        "font-bold inline-flex items-center justify-center gap-2 transition-all select-none cursor-pointer",
        "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white",
        "shadow-[0_6px_16px_-6px_rgba(16,185,129,0.5)] hover:shadow-[0_8px_20px_-6px_rgba(16,185,129,0.6)] hover:translate-y-[-1px] active:translate-y-0 active:scale-[0.98]",
        "disabled:opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none",
        iconOnly ? sizeConfig.iconBtn : sizeConfig.textBtn,
        className
      )}
      {...rest}
    >
      {iconElement}
      {!iconOnly && (children || label)}
    </button>
  );
};

export const RejectButton: React.FC<RejectButtonProps> = ({
  onReject,
  onClick,
  loading = false,
  disabled = false,
  iconOnly = false,
  size = "md",
  label = "Reject",
  icon,
  className = "",
  children,
  ...rest
}) => {
  const isBlocked = disabled || loading;
  const sizeConfig = SIZE_STYLES[size] || SIZE_STYLES.md;
  const iconElement = icon || <XCircle className={sizeConfig.icon} />;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isBlocked) return;
    if (onReject) onReject(e);
    if (onClick) onClick(e);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isBlocked}
      aria-label={label}
      className={cn(
        "font-bold inline-flex items-center justify-center gap-2 transition-all select-none cursor-pointer",
        "border border-red-200 bg-white text-red-600",
        "hover:bg-red-50 hover:border-red-300 active:scale-[0.98]",
        "disabled:opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:transform-none",
        iconOnly
          ? sizeConfig.iconBtn
          : cn(
              sizeConfig.textBtn,
              size === "md" ? "px-4" : "" // exact matching figma px-4 for reject in md
            ),
        className
      )}
      {...rest}
    >
      {iconElement}
      {!iconOnly && (children || label)}
    </button>
  );
};

const ApproveRejectActions: React.FC<ApproveRejectActionsProps> = ({
  onApprove,
  onReject,
  loading = false,
  approveLoading = false,
  rejectLoading = false,
  disabled = false,
  iconOnly = false,
  rejectIconOnly = false,
  approveIconOnly = false,
  size = "md",
  approveLabel = "Approve",
  rejectLabel = "Reject",
  approveIcon,
  rejectIcon,
  className = "",
  approveClassName = "",
  rejectClassName = "",
}) => {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <RejectButton
        onReject={onReject}
        loading={loading || rejectLoading}
        disabled={disabled}
        iconOnly={iconOnly || rejectIconOnly}
        size={size}
        label={rejectLabel}
        icon={rejectIcon}
        className={rejectClassName}
      />
      <ApproveButton
        onApprove={onApprove}
        loading={loading || approveLoading}
        disabled={disabled}
        iconOnly={iconOnly || approveIconOnly}
        size={size}
        label={approveLabel}
        icon={approveIcon}
        className={approveClassName}
      />
    </div>
  );
};

export default ApproveRejectActions;